import { useNavigate } from "react-router-dom";
import {
    Brain, BarChart3, Users, Shield, FileText, ArrowRight, ChevronRight,
    Target, TrendingUp, Eye, Layers, Zap,
    AlertCircle, TrendingDown, UserPlus, ClipboardList, PieChart, Rocket
} from "lucide-react";
import { Reveal, SlideIn } from "./animations";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_46ba8d0f-50d1-45b0-932d-164e40ecbfdf/artifacts/l81b46nl_K2B_Logo_Final-2.png";

export function ProblemSection() {
    return (
        <section data-testid="problem-section" className="py-20 sm:py-28 px-6 sm:px-8 bg-[#F8FAFC]">
            <div className="max-w-7xl mx-auto">
                <Reveal>
                    <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#4A7AB5] mb-4 text-center">The Problem</p>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 text-center mb-14 sm:mb-16" style={{ fontFamily: "Outfit" }}>
                        Why Most Coaching Fails
                    </h2>
                </Reveal>
                <div className="grid sm:grid-cols-3 gap-6 sm:gap-8 max-w-4xl mx-auto">
                    {[
                        { icon: AlertCircle, title: "Feedback is understood — not acted on", desc: "Leaders agree in sessions but revert under pressure. Understanding is not the same as doing." },
                        { icon: TrendingDown, title: "Training creates awareness — not change", desc: "Awareness without behavioral readiness is a sunk cost. Most programs never bridge this gap." },
                        { icon: Target, title: "Coachability is assumed — never measured", desc: "Organizations invest before diagnosing readiness. No one asks: will this person actually act?" },
                    ].map((problemItem, i) => (
                        <SlideIn key={problemItem.title} delay={i * 0.12}>
                            <div className="text-center sm:text-left">
                                <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center mx-auto sm:mx-0 mb-5 shadow-sm">
                                    <problemItem.icon className="h-5 w-5 text-[#4A7AB5]" strokeWidth={1.5} />
                                </div>
                                <h3 className="font-semibold text-slate-900 text-[0.95rem] mb-2" style={{ fontFamily: "Outfit" }}>{problemItem.title}</h3>
                                <p className="text-sm text-slate-500 leading-relaxed">{problemItem.desc}</p>
                            </div>
                        </SlideIn>
                    ))}
                </div>
            </div>
        </section>
    );
}

export function PhilosophySection() {
    return (
        <section data-testid="philosophy-section" className="py-20 sm:py-28 px-6 sm:px-8 relative overflow-hidden">
            <div className="max-w-7xl mx-auto relative z-10">
                <Reveal>
                    <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#4A7AB5] mb-4 text-center">Our Philosophy</p>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 text-center mb-4" style={{ fontFamily: "Outfit" }}>
                        From Knowledge to Action
                    </h2>
                    <p className="text-base sm:text-lg text-slate-500 text-center max-w-xl mx-auto mb-14 sm:mb-16">
                        Organizations don't lack knowledge. They lack execution.<br className="hidden sm:block" />
                        We measure whether people will act.
                    </p>
                </Reveal>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-0 max-w-3xl mx-auto">
                    {[
                        { label: "Knowledge", sub: "What they know", accent: false },
                        { label: "Insight", sub: "What they understand", accent: false },
                        { label: "Action", sub: "What they do", accent: true },
                        { label: "Business Impact", sub: "What changes", accent: true },
                    ].map((step, i) => (
                        <SlideIn key={step.label} delay={0.1 + i * 0.12} className="flex items-center">
                            <div className={`px-5 sm:px-6 py-4 rounded-xl text-center border transition-all min-w-[140px] sm:min-w-[160px] ${
                                step.accent
                                    ? "bg-[#4A7AB5] border-[#4A7AB5] shadow-lg shadow-[#4A7AB5]/20"
                                    : "bg-white border-slate-200 shadow-sm"
                            }`}>
                                <p className={`font-semibold text-sm mb-0.5 ${step.accent ? "text-white" : "text-slate-900"}`} style={{ fontFamily: "Outfit" }}>{step.label}</p>
                                <p className={`text-[0.7rem] ${step.accent ? "text-white/70" : "text-slate-400"}`}>{step.sub}</p>
                            </div>
                            {i < 3 && (
                                <ChevronRight className={`h-5 w-5 mx-1 hidden sm:block flex-shrink-0 ${i >= 1 ? "text-[#4A7AB5]" : "text-slate-300"}`} />
                            )}
                        </SlideIn>
                    ))}
                </div>
                <Reveal delay={0.6}>
                    <p className="text-center mt-12 sm:mt-14">
                        <span className="inline-block px-5 py-2.5 rounded-full bg-[#F5A623]/10 text-[#F5A623] font-bold text-sm tracking-wide" style={{ fontFamily: "Outfit" }}>
                            Creating Actionable Learning
                        </span>
                    </p>
                </Reveal>
            </div>
        </section>
    );
}

export function SolutionSection() {
    return (
        <section data-testid="solution-section" className="py-20 sm:py-28 px-6 sm:px-8 bg-[#F8FAFC]">
            <div className="max-w-7xl mx-auto">
                <Reveal>
                    <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#4A7AB5] mb-4 text-center">The Solution</p>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 text-center mb-14 sm:mb-16" style={{ fontFamily: "Outfit" }}>
                        Measure Action Readiness — Not Just Intent
                    </h2>
                </Reveal>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-5xl mx-auto">
                    {[
                        { icon: Brain, label: "Scenario-Based", desc: "25 realistic workplace scenarios, not opinion questions" },
                        { icon: Layers, label: "6 Dimensions", desc: "From ego management to follow-through commitment" },
                        { icon: PieChart, label: "5 Archetypes", desc: "Clear profiles from growth-ready to high-resistance" },
                        { icon: Zap, label: "3 Indices", desc: "CRI, Action Potential, and Defense scoring" },
                    ].map((c, i) => (
                        <SlideIn key={c.label} delay={i * 0.1}>
                            <div className="bg-white rounded-xl border border-slate-200/80 p-6 h-full hover:border-[#4A7AB5]/30 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group">
                                <div className="w-10 h-10 rounded-lg bg-[#4A7AB5]/8 flex items-center justify-center mb-4 group-hover:bg-[#4A7AB5]/15 transition-colors">
                                    <c.icon className="h-5 w-5 text-[#4A7AB5]" strokeWidth={1.5} />
                                </div>
                                <p className="font-semibold text-slate-900 text-sm mb-1.5" style={{ fontFamily: "Outfit" }}>{c.label}</p>
                                <p className="text-[0.8rem] text-slate-500 leading-relaxed">{c.desc}</p>
                            </div>
                        </SlideIn>
                    ))}
                </div>
            </div>
        </section>
    );
}

export function FeaturesSection() {
    return (
        <section id="features" data-testid="features-section" className="py-20 sm:py-28 px-6 sm:px-8">
            <div className="max-w-7xl mx-auto">
                <Reveal>
                    <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#4A7AB5] mb-4 text-center">Platform</p>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 text-center mb-14 sm:mb-16" style={{ fontFamily: "Outfit" }}>
                        Built for Enterprise Coaching
                    </h2>
                </Reveal>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 max-w-5xl mx-auto">
                    {[
                        { icon: Brain, title: "Assessment Engine", desc: "Psychology-driven behavioral scoring across 6 dimensions" },
                        { icon: BarChart3, title: "Visual Reports", desc: "Radar charts, score gauges, and archetype mapping" },
                        { icon: Eye, title: "Coach Dashboard", desc: "User management, assessment tracking, and coaching notes" },
                        { icon: Shield, title: "Admin Control", desc: "Org-wide analytics, filtering, and role management" },
                        { icon: FileText, title: "PDF Export", desc: "Branded diagnostic reports for stakeholders" },
                        { icon: Users, title: "Token Access", desc: "No-login assessment links for frictionless participation" },
                    ].map((f, i) => (
                        <SlideIn key={f.title} delay={i * 0.08}>
                            <div className="flex gap-4 p-5 rounded-xl border border-slate-100 bg-white hover:border-[#4A7AB5]/20 hover:shadow-md transition-all duration-300 group">
                                <div className="w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0 group-hover:bg-[#4A7AB5]/8 transition-colors">
                                    <f.icon className="h-5 w-5 text-[#4A7AB5]" strokeWidth={1.5} />
                                </div>
                                <div>
                                    <p className="font-semibold text-slate-900 text-sm mb-1" style={{ fontFamily: "Outfit" }}>{f.title}</p>
                                    <p className="text-[0.8rem] text-slate-500 leading-relaxed">{f.desc}</p>
                                </div>
                            </div>
                        </SlideIn>
                    ))}
                </div>
            </div>
        </section>
    );
}

export function HowItWorksSection() {
    return (
        <section id="how-it-works" data-testid="how-it-works-section" className="py-20 sm:py-28 px-6 sm:px-8 bg-[#F8FAFC]">
            <div className="max-w-7xl mx-auto">
                <Reveal>
                    <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#4A7AB5] mb-4 text-center">Process</p>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 text-center mb-14 sm:mb-16" style={{ fontFamily: "Outfit" }}>
                        How It Works
                    </h2>
                </Reveal>
                <div className="max-w-3xl mx-auto relative">
                    <div className="absolute left-6 sm:left-7 top-0 bottom-0 w-px bg-slate-200 hidden sm:block" />
                    {[
                        { icon: UserPlus, num: "01", title: "Create Coaches", desc: "Set up coach accounts and configure organization structure." },
                        { icon: Users, num: "02", title: "Add Users", desc: "Create assessment users or generate tokenized invite links." },
                        { icon: ClipboardList, num: "03", title: "Take Assessment", desc: "Respondents complete 25 scenario-based questions. Clean, jargon-free." },
                        { icon: BarChart3, num: "04", title: "Generate Report", desc: "Automatic scoring across 6 dimensions with archetype classification." },
                        { icon: Rocket, num: "05", title: "Drive Action", desc: "Use behavioral insights to make evidence-based coaching decisions." },
                    ].map((s, i) => (
                        <SlideIn key={s.num} delay={i * 0.1}>
                            <div className="flex gap-5 sm:gap-8 mb-8 last:mb-0 relative">
                                <div className="relative z-10 w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-white border border-slate-200 flex items-center justify-center flex-shrink-0 shadow-sm">
                                    <s.icon className="h-5 w-5 text-[#4A7AB5]" strokeWidth={1.5} />
                                </div>
                                <div className="pt-1.5">
                                    <p className="text-[0.7rem] uppercase tracking-[0.15em] font-bold text-[#4A7AB5] mb-1">{s.num}</p>
                                    <h3 className="font-semibold text-slate-900 text-[0.95rem] mb-1" style={{ fontFamily: "Outfit" }}>{s.title}</h3>
                                    <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
                                </div>
                            </div>
                        </SlideIn>
                    ))}
                </div>
            </div>
        </section>
    );
}

export function OutcomesSection() {
    return (
        <section id="outcomes" data-testid="outcomes-section" className="py-20 sm:py-28 px-6 sm:px-8 relative overflow-hidden">
            <div className="max-w-7xl mx-auto relative z-10">
                <Reveal>
                    <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#4A7AB5] mb-4 text-center">Outcomes</p>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 text-center mb-5" style={{ fontFamily: "Outfit" }}>
                        Move from Learning to Doing
                    </h2>
                    <p className="text-base text-slate-500 text-center max-w-lg mx-auto mb-14">
                        We don't measure what people know. We measure whether they will act.
                    </p>
                </Reveal>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-4xl mx-auto">
                    {[
                        { icon: Target, statement: "Identify who will act" },
                        { icon: TrendingUp, statement: "Focus coaching where it matters" },
                        { icon: Zap, statement: "Reduce wasted investment" },
                        { icon: BarChart3, statement: "Drive measurable behavior change" },
                    ].map((o, i) => (
                        <SlideIn key={o.statement} delay={i * 0.1}>
                            <div className="bg-[#F8FAFC] border border-slate-200/80 rounded-xl p-6 text-center hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                                <div className="w-11 h-11 rounded-lg bg-[#4A7AB5]/8 flex items-center justify-center mx-auto mb-4">
                                    <o.icon className="h-5 w-5 text-[#4A7AB5]" strokeWidth={1.5} />
                                </div>
                                <p className="font-semibold text-slate-900 text-sm leading-snug" style={{ fontFamily: "Outfit" }}>{o.statement}</p>
                            </div>
                        </SlideIn>
                    ))}
                </div>
            </div>
        </section>
    );
}

export function TrustSection() {
    return (
        <section data-testid="trust-section" className="py-20 sm:py-28 px-6 sm:px-8 bg-[#0F172A] relative overflow-hidden">
            <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_30%_50%,#4A7AB5_0%,transparent_50%)]" />
            <div className="max-w-7xl mx-auto relative z-10 text-center">
                <Reveal>
                    <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#4A7AB5] mb-5">Our Position</p>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white mb-6 max-w-2xl mx-auto leading-snug" style={{ fontFamily: "Outfit" }}>
                        Not a Personality Test.<br />Not a Survey.
                    </h2>
                    <p className="text-base text-slate-400 leading-relaxed max-w-xl mx-auto mb-10">
                        A structured behavioral diagnostic designed for corporate coaching contexts. We measure how people respond to feedback, authority, and accountability — not who they think they are.
                    </p>
                </Reveal>
                <Reveal delay={0.15}>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
                        {[
                            "Scenario-based, not self-reported",
                            "Psychometrically weighted",
                            "Enterprise-grade privacy",
                        ].map((t) => (
                            <div key={t} className="flex items-center gap-2.5 px-4 py-2.5 rounded-full border border-slate-700 bg-slate-800/50">
                                <div className="w-1.5 h-1.5 rounded-full bg-[#4A7AB5]" />
                                <span className="text-[0.8rem] text-slate-300 font-medium">{t}</span>
                            </div>
                        ))}
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

export function CTASection({ onDemo }) {
    const navigate = useNavigate();
    return (
        <section id="cta" data-testid="cta-section" className="py-20 sm:py-28 px-6 sm:px-8">
            <div className="max-w-3xl mx-auto text-center">
                <Reveal>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900 leading-snug mb-5" style={{ fontFamily: "Outfit" }}>
                        Stop Investing in Learning That<br className="hidden sm:block" /> Doesn't Translate to Action
                    </h2>
                    <p className="text-base text-slate-500 leading-relaxed max-w-lg mx-auto mb-8">
                        Start diagnosing who will actually create business impact.
                    </p>
                </Reveal>
                <Reveal delay={0.12}>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                        <button data-testid="cta-demo-btn" onClick={onDemo}
                            className="w-full sm:w-auto text-sm font-semibold text-white bg-[#F5A623] hover:bg-[#d98f1c] px-8 py-3.5 rounded-lg transition-all shadow-sm hover:shadow-lg hover:-translate-y-0.5 flex items-center justify-center gap-2">
                            Request Demo <ArrowRight className="h-4 w-4" />
                        </button>
                        <button data-testid="cta-login-btn" onClick={() => navigate("/login")}
                            className="w-full sm:w-auto text-sm font-medium text-slate-600 border-2 border-slate-200 hover:border-slate-300 px-8 py-3.5 rounded-lg transition-all hover:-translate-y-0.5">
                            Login to Platform
                        </button>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}

export function FooterSection() {
    return (
        <footer data-testid="homepage-footer" className="py-12 sm:py-16 px-6 sm:px-8 border-t border-slate-100">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <img src={LOGO_URL} alt="K2B" className="h-8 w-8" />
                        <p className="text-sm text-slate-400 leading-relaxed">Transcending Knowledge into Business</p>
                    </div>
                    <p className="text-xs text-slate-400">&copy; {new Date().getFullYear()} K2B Learning. All rights reserved.</p>
                </div>
            </div>
        </footer>
    );
}
